/**
 * Relevance floor / weak-match partition for search results (U7 SEARCH slice,
 * ui-design-review-2026-07-05.md P1 item 1: "never present 0.02-score results
 * as peers of real hits; show an honest count instead of '50+'").
 *
 * SCORE SEMANTICS (inspected 2026-07-05 — backend/aletheia/retrieval/core.py +
 * config.py + api/core/projection.py):
 *
 *   - Hybrid / lexical / semantic results carry the **RRF fused score**:
 *     `score = Σ_retrievers w_r / (RRF_K + rank_r)` with `RRF_K = 60` and
 *     weights lexical=1.0, semantic=1.0, tag=0.5. The score encodes RANK
 *     AGREEMENT, not similarity: a rank-1 single-list hit is `1/61 ≈ 0.0164`,
 *     a rank-1 dual-list hit ≈ `0.033`, a rank-50 straggler ≈ `0.009`. The
 *     whole scale lives below ~0.05 — which is why the UI showed "0.02"
 *     everywhere and why an absolute 0.1 floor would fold EVERYTHING.
 *   - `search_by_metadata` (pure-filter) results carry a flat `1.0` (core.py:
 *     "a pure-filter request has no relevance gradient yet").
 *   - A rerank score (cross-encoder, 0..1) would replace the RRF score, but
 *     rerank was dropped with the local-ML pipeline (RENDER_DEFAULTS.
 *     searchRerankDefault = false); the branch is kept for wire honesty.
 *
 * THE RULE (scale-aware, relative-to-top — documented for the fold tooltip):
 *
 *   RRF regime (topScore < 0.2 — rank-fused scores):
 *     - If even the TOP hit is below `1/(60+15) ≈ 0.0135` (i.e. the best
 *       evidence is a lone-list hit deeper than rank ~15, or a lone tag
 *       match), the whole set is weak → the caller shows the honest
 *       "no strong matches" header.
 *     - Otherwise a result is weak when `score < min(0.012, 0.55 × top)`.
 *       `0.012 = 1/(60+~24)`: a lone-list hit at rank ≳24 with no second
 *       retriever agreeing. The `0.55 × top` term self-scales: when the top
 *       hit has dual-retriever agreement (top ≈ 0.033) the floor caps at
 *       0.012; when the whole set is lone-list (top ≈ 0.0164) the floor
 *       relaxes to ~0.009 (≈ rank 50+) so ordinary single-mode searches
 *       don't fold their own mid-ranks.
 *
 *   Normalized regime (topScore ≥ 0.2 — rerank or flat metadata scores):
 *     - weak when `score < max(0.1, 0.15 × top)` — the review's suggested
 *       rule, valid on a 0..1 scale. Flat metadata sets (all 1.0) fold
 *       nothing.
 *
 * Pure module — no React, no DOM — so it is unit-testable under the node
 * vitest environment (vitest.config.ts).
 */

import type { ResultItem } from '@/types';

/** Above this top score we assume a normalized 0..1 scale (rerank / flat metadata). */
export const RRF_SCALE_CEILING = 0.2;
/** RRF regime: absolute weak floor ≈ a lone-retriever hit at rank ~24 (1/(60+24)). */
export const RRF_WEAK_FLOOR = 0.012;
/** RRF regime: if the TOP score is below a lone-list rank-~15 hit, nothing is strong. */
export const RRF_ALL_WEAK_TOP = 0.0135;
/** RRF regime: relative fraction of the top score below which a hit is weak. */
export const RRF_RELATIVE_FRACTION = 0.55;
/** Normalized regime: absolute weak floor. */
export const NORM_WEAK_FLOOR = 0.1;
/** Normalized regime: relative fraction of the top score below which a hit is weak. */
export const NORM_RELATIVE_FRACTION = 0.15;

export interface RelevancePartition {
  /** Results at or above the floor, original order preserved. */
  strong: ResultItem[];
  /** Results below the floor — fold these under the disclosure. */
  weak: ResultItem[];
  /** True when the ENTIRE set is weak (show the honest header, list them plainly). */
  allWeak: boolean;
}

/**
 * The weak-score floor for a result set whose best score is `topScore`.
 * Exposed for tests; `partitionByRelevance` is the consumer.
 */
export function weakScoreFloor(topScore: number): number {
  if (topScore >= RRF_SCALE_CEILING) {
    return Math.max(NORM_WEAK_FLOOR, NORM_RELATIVE_FRACTION * topScore);
  }
  return Math.min(RRF_WEAK_FLOOR, RRF_RELATIVE_FRACTION * topScore);
}

/** Split `items` into strong / weak per the documented rule. Order is preserved. */
export function partitionByRelevance(items: ResultItem[]): RelevancePartition {
  if (items.length === 0) return { strong: [], weak: [], allWeak: false };

  const topScore = items.reduce((m, it) => (it.score > m ? it.score : m), -Infinity);

  // Degenerate scores (all zero / negative — e.g. structural listings that
  // stamp score=0.0): no gradient to judge, so present everything as-is.
  if (!Number.isFinite(topScore) || topScore <= 0) {
    return { strong: items, weak: [], allWeak: false };
  }

  // RRF regime with a hopeless top: even the best hit is a deep lone-list
  // straggler — the honest presentation is "no strong matches".
  if (topScore < RRF_SCALE_CEILING && topScore < RRF_ALL_WEAK_TOP) {
    return { strong: [], weak: [...items], allWeak: true };
  }

  const floor = weakScoreFloor(topScore);
  const strong: ResultItem[] = [];
  const weak: ResultItem[] = [];
  for (const item of items) {
    (item.score < floor ? weak : strong).push(item);
  }
  // By construction the top item is never below the floor, so `strong` is
  // non-empty here — `allWeak` only arises via the hopeless-top branch.
  return { strong, weak, allWeak: false };
}

/**
 * The results-head count line. Exact when we can be exact:
 *   - all pages loaded            → "23 matches · 18 strong" (loaded IS the total)
 *   - more pages + backend total  → "61 matches · 18 strong"
 *   - more pages, no total        → "50+ matches · 42 strong"
 * The "· N strong" segment only appears when a weak fold exists.
 */
export function formatMatchCount(args: {
  loaded: number;
  strong: number;
  weak: number;
  totalEstimate: number | null;
  hasMore: boolean;
}): string {
  const exactTotal = !args.hasMore
    ? args.loaded
    : args.totalEstimate !== null
      ? Math.max(args.totalEstimate, args.loaded)
      : null;
  const totalStr = exactTotal !== null ? `${exactTotal}` : `${args.loaded}+`;
  const noun = exactTotal === 1 ? 'match' : 'matches';
  if (args.weak === 0) return `${totalStr} ${noun}`;
  return `${totalStr} ${noun} · ${args.strong} strong`;
}

/** The honest header when the entire result set is weak. */
export function formatAllWeakHeader(weakCount: number): string {
  return `No strong matches — showing ${weakCount} weak lexical overlap${
    weakCount === 1 ? '' : 's'
  }`;
}
